import {
  createDefault,
  vArray,
  vBoolean,
  vDate,
  vEmail,
  vEmailOrEmpty,
  vFallback,
  vFallbackObj,
  vInt,
  vLiteral,
  vMilitaryTimeStrict,
  vMinValue,
  vNumber,
  vObject,
  vOmit,
  vOpt,
  vOptional,
  type vOutput,
  vPicklist,
  vPipe,
  vString,
  vVariant,
  vWorkflow,
} from '../validation/index.ts'
import {
  distanceProviders,
  providerVersions,
  routingTypes,
} from './account-settings-routing.ts'
import { messageMap, workflows } from './common.ts'
import { labelCountSchema, labelSizeSchema, labelTypes } from './labels.ts'
import { WEBHOOK_EVENT_VALUES } from './webhook.ts'

const carrierEmailTypes = ['all', ...messageMap['spot-quote']] as const

const auditThresholdTypes = ['flat', 'percent'] as const

const addressBookSettingsSchema = vObject({
  disableAutoSaveAddresses: vFallback(vBoolean(), false),
})
const bookingSettingsSchema = vObject({
  poNumberRequired: vFallback(vBoolean(), false),
  rememberSpecialInstructionsDest: vFallback(vBoolean(), true),
  rememberSpecialInstructionsOrigin: vFallback(vBoolean(), true),
  shipperNumberRequired: vFallback(vBoolean(), false),
  showBillToOnBooking: vFallback(vBoolean(), false),
  requireReasonForQuoteChoice: vFallback(vBoolean(), false),
  disableAutoDeliver: vFallback(vBoolean(), false),
})
const auditThresholdSettingsSchema = vObject({
  enabled: vFallback(vBoolean(), false),
  type: vPicklist(auditThresholdTypes),
  threshold: vNumber(),
})
const invoiceAutomationSettingsSchema = vObject({
  ltl: vOptional(auditThresholdSettingsSchema),
  parcel: vOptional(auditThresholdSettingsSchema),
  truckload: vOptional(auditThresholdSettingsSchema),
})
const invoiceSettingsSchema = vObject({
  autoArchiveShipment: vFallback(vBoolean(), false),
  emailDispute: vFallback(vBoolean(), true),
  automation: vOptional(invoiceAutomationSettingsSchema),
})

const notificationSettingsSchema = vObject({
  shareMissedPickup: vFallback(vBoolean(), false),
})
const ltlCatalogSettingsSchema = vObject({
  class: vFallback(vBoolean(), true),
  dims: vFallback(vBoolean(), false),
  hazardous: vFallback(vBoolean(), true),
  nmfc: vFallback(vBoolean(), false),
  package: vFallback(vBoolean(), true),
  quantity: vFallback(vBoolean(), false),
  saidToContain: vFallback(vBoolean(), true),
  stackable: vFallback(vBoolean(), false),
  weight: vFallback(vBoolean(), false),
})

const parcelCatalogSettingsSchema = vObject({
  declaredValue: vFallback(vBoolean(), false),
  dims: vFallback(vBoolean(), false),
  nonStandard: vFallback(vBoolean(), false),
  package: vFallback(vBoolean(), true),
  weight: vFallback(vBoolean(), false),
})
const productCatalogSettingsSchema = vObject({
  autoItemCreate: vFallback(vBoolean(), true),
  canQuoteCustomProduct: vFallback(vBoolean(), true),
  ltl: ltlCatalogSettingsSchema,
  parcel: parcelCatalogSettingsSchema,
})

const ltlQuoteSettingsSchema = vObject({
  hideBrokerForDirectRates: vFallback(vBoolean(), false),
  requireDims: vFallback(vBoolean(), false),
  requireNmfc: vFallback(vBoolean(), false),
})
const parcelQuoteSettingsSchema = vObject({
  requireDims: vFallback(vBoolean(), false),
})
const quotingSettingsSchema = vObject({
  ltl: vFallbackObj(ltlQuoteSettingsSchema),
  parcel: vFallbackObj(parcelQuoteSettingsSchema),
  showPricingIndicator: vFallback(vBoolean(), false),
  showBidExpiration: vFallback(vBoolean(), false),
  defaultWorkflow: vFallback(vPicklist(workflows), 'ltl'),
})
const ltlShippingSettingsSchema = vObject({
  preferredLabelCount: vFallback(labelCountSchema, 1),
  preferredLabelSize: vFallback(labelSizeSchema, '4x6'),
  rememberLabelSize: vFallback(vBoolean(), false),
})
const tlShippingSettingsSchema = vObject({
  preferredLabelCount: vOptional(labelCountSchema),
  preferredLabelSize: vOptional(labelSizeSchema),
  rememberLabelSize: vFallback(vBoolean(), false),
})

const parcelShippingSettingsSchema = vObject({
  preferredLabelType: vFallback(vPicklist(labelTypes), 'pdf'),
})

export const preferredBolTypes = ['carrier', 'fv'] as const
export type PreferredBolType = (typeof preferredBolTypes)[number]

const shippingSettingsSchema = vObject({
  preferredBol: vFallback(vPicklist(preferredBolTypes), 'fv'),
  ltl: vFallbackObj(ltlShippingSettingsSchema),
  tl: vFallback(
    vOptional(tlShippingSettingsSchema),
    createDefault(tlShippingSettingsSchema),
  ),
  parcel: vFallback(
    vOptional(parcelShippingSettingsSchema),
    createDefault(parcelShippingSettingsSchema),
  ),
})
const tagSettingsSchema = vObject({
  preventUsersCreateTags: vFallback(vBoolean(), false),
})
const glCodeSettingsSchema = vObject({
  enabled: vFallback(vBoolean(), true),
  required: vFallback(vBoolean(), false),
})
export const routingSettingsSchema = vObject({
  distanceProvider: vFallback(vPicklist(distanceProviders), 'pc-miler'),
  distanceProviderVersion: vFallback(vPicklist(providerVersions), 'Current'),
  routingType: vFallback(vPicklist(routingTypes), 'practical'),
  bordersOpen: vFallback(vBoolean(), true),
})
const spotQuoteSettingsSchema = vObject({
  disabledCarrierEmails: vArray(vPicklist(carrierEmailTypes)),
})
const truckloadSettings = vObject({
  enableMexico: vFallback(vBoolean(), false),
  allowAppointmentUpdates: vFallback(vBoolean(), true),
})
const routeGuideSettingsSchema = vObject({
  isEnabled: vFallback(vBoolean(), false),
  defaultStepTimeoutMinutes: vFallback(
    vPipe(vNumber(), vInt(), vMinValue(1)),
    90,
  ),
  respectBusinessHours: vFallback(vBoolean(), false),
  businessHoursStart: vFallback(vMilitaryTimeStrict, '08:00'),
  businessHoursEnd: vFallback(vMilitaryTimeStrict, '17:00'),
  runOnSaturday: vFallback(vBoolean(), false),
  runOnSunday: vFallback(vBoolean(), false),
  includeTpeRates: vFallback(vBoolean(), false),
})
const fuelSettingsSchema = vFallback(
  vVariant('provider', [
    vObject({
      provider: vLiteral('breakthrough'),
      shipperId: vString(),
      divisionId: vString(),
      username: vString(),
      password: vOptional(vString()),
      enableEstimates: vOpt(vBoolean(), false),
      submitModes: vOpt(vArray(vWorkflow()), []),
    }),
    vObject({
      provider: vLiteral('us-eia'),
    }),
  ]),
  {
    provider: 'us-eia',
  },
)

const webhookSettingsSchema = vObject({
  url: vOptional(vString()),
  sandboxUrl: vOptional(vString()),
  signingSecret: vOptional(vString()),
  enabled: vOptional(vBoolean()),
  sandboxEnabled: vOptional(vBoolean()),
  createdDate: vOptional(vDate()),
  events: vOptional(vArray(vPicklist(WEBHOOK_EVENT_VALUES))),
  failingSince: vOptional(vDate()),
  sandboxFailingSince: vOptional(vDate()),
})

export const shipperAccountSettingsSchema = vObject({
  addressBook: vFallbackObj(addressBookSettingsSchema),
  booking: vFallbackObj(bookingSettingsSchema),
  invoicing: vFallbackObj(invoiceSettingsSchema),
  notifications: vFallbackObj(notificationSettingsSchema),
  productCatalog: vFallbackObj(productCatalogSettingsSchema),
  quoting: vFallbackObj(quotingSettingsSchema),
  shipping: vFallbackObj(shippingSettingsSchema),
  tags: vFallbackObj(tagSettingsSchema),
  glCode: vFallbackObj(glCodeSettingsSchema),
  routing: vFallbackObj(routingSettingsSchema),
  spotQuote: vFallbackObj(spotQuoteSettingsSchema),
  truckload: vFallbackObj(truckloadSettings),
  routeGuide: vFallbackObj(routeGuideSettingsSchema),
  fuel: vFallbackObj(fuelSettingsSchema),
  technicalContactEmail: vFallback(vEmailOrEmpty, ''),
  webhook: vFallbackObj(webhookSettingsSchema),
})

export const shipperAccountSettingsUpdateSchema = vOmit(
  shipperAccountSettingsSchema,
  ['webhook'],
)

export const provisionerAccountCreationSchema = vObject({
  email: vEmail,
  password: vString(),
})

export type InvoicingSettings = vOutput<typeof invoiceSettingsSchema>
export type InvoiceAutomationSettings = vOutput<
  typeof invoiceAutomationSettingsSchema
>
export type AuditThresholdSettings = vOutput<
  typeof auditThresholdSettingsSchema
>
export type LTLCatalogSettings = vOutput<typeof ltlCatalogSettingsSchema>
export type ParcelCatalogSettings = vOutput<typeof parcelCatalogSettingsSchema>

export type BookingSettings = vOutput<typeof bookingSettingsSchema>
export type WebhookSettings = vOutput<typeof webhookSettingsSchema>
export type AccountSettings = vOutput<typeof shipperAccountSettingsSchema>

export type ProvisionerAccountCreationRequest = vOutput<
  typeof provisionerAccountCreationSchema
>
